import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
export const API_ORIGIN = BACKEND_URL;

/**
 * iter253 — Resolve a backend-hosted media URL for use in `<img src>` / `<a href>`.
 * Two shapes to handle:
 *   • Absolute (`https://…`) → returned as-is
 *   • Backend-relative (`/files/…` or any other path) → joined against `${BACKEND}/api`
 * iter255 — legacy `/api/files/…` rows have been backfilled to `/files/…`, so
 * this helper no longer strips a redundant leading `/api/`. If new legacy rows
 * ever appear, the pre-backfill guard can be restored here.
 */
export function resolveMediaUrl(url) {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("http")) return url;
  return `${API_BASE}${url}`;
}

// Cookie-based auth: backend sets httpOnly access_token + refresh_token cookies on /auth/login.
// `withCredentials: true` ensures the browser sends them on every API call.
const api = axios.create({ baseURL: API_BASE, withCredentials: true });

// Impersonation: when an admin issues a "View as" token via POST /api/admin/impersonate/{id},
// the FE stores that token in sessionStorage (tab-scoped) and attaches it as a
// Bearer header on every subsequent request. The cookie session for the real
// admin is untouched — closing the tab (or calling stopImpersonation()) ends
// the shadow session cleanly.
export const IMPERSONATION_KEY = "iris.impersonation.token";
export const IMPERSONATION_META = "iris.impersonation.meta";

api.interceptors.request.use((config) => {
  try {
    const t = sessionStorage.getItem(IMPERSONATION_KEY);
    if (t) {
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
      // Skip cookie credentials for impersonated calls so the backend doesn't
      // prefer the admin cookie over the shadow token.
      config.withCredentials = false;
    }
  } catch (err) {
    // sessionStorage disabled (private mode) → fall back to cookie session.
    void err;
  }
  return config;
});

export function startImpersonation(token, targetMeta) {
  try {
    sessionStorage.setItem(IMPERSONATION_KEY, token);
    sessionStorage.setItem(IMPERSONATION_META, JSON.stringify(targetMeta || {}));
  } catch (err) {
    void err;
  }
}

export function stopImpersonation() {
  try {
    sessionStorage.removeItem(IMPERSONATION_KEY);
    sessionStorage.removeItem(IMPERSONATION_META);
  } catch (err) {
    void err;
  }
}

export function currentImpersonation() {
  try {
    const t = sessionStorage.getItem(IMPERSONATION_KEY);
    if (!t) return null;
    const meta = sessionStorage.getItem(IMPERSONATION_META);
    return { token: t, meta: meta ? JSON.parse(meta) : null };
  } catch (err) {
    void err;
    return null;
  }
}

/**
 * Safe-stringify an axios error's response.data.detail for display in toasts.
 * FastAPI returns either a plain string (HTTPException) or an array of validation
 * error objects (ValidationError). Passing the latter directly into a React
 * toast renderer crashes the page.
 */
export function errorMessage(err, fallback = "Something went wrong") {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === "string" ? x : (x?.msg || JSON.stringify(x)))).join(", ");
  }
  if (typeof d === "object") return d.msg || JSON.stringify(d);
  return String(d);
}

export default api;
